import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertCircle, Loader2, Settings, Database, Trash2 } from 'lucide-react';
import { Client } from '@/types/client';
import { useToast } from '@/hooks/use-toast';
import api from '@/lib/api';
import { useConfirmation } from '@/hooks/useConfirmation';

interface SouthIntegrationProps {
  client: Client;
}

interface SouthConfig {
  system_token: string;
  api_base_url: string;
}

interface SouthIntegrationStatus {
  active: boolean;
  has_token: boolean;
  has_api_url: boolean;
  system_management: string;
}

const SouthIntegration: React.FC<SouthIntegrationProps> = ({ client }) => {
  const [integrationStatus, setIntegrationStatus] = useState<SouthIntegrationStatus>({
    active: false,
    has_token: false,
    has_api_url: false,
    system_management: ''
  });
  
  const { confirm, ConfirmationDialog } = useConfirmation();

  const [config, setConfig] = useState<SouthConfig>({
    system_token: '',
    api_base_url: ''
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  const { toast } = useToast();

  // Verificar status da integração ao carregar
  useEffect(() => {
    if (client?.id) {
      checkIntegrationStatus();
    }
  }, [client?.id]);

  const checkIntegrationStatus = async () => {
    try {
      setLoading(true);
      console.log(`🔍 [South] Verificando status da integração para cliente ${client.id}`);
      
      const response = await api.get(`/south/check-integration?client_id=${client.id}`);
      
      if (response.success) {
        setIntegrationStatus({
          active: response.active,
          has_token: response.has_token,
          has_api_url: response.has_api_url,
          system_management: response.system_management
        });

        // Se já tem configuração, preencher os campos (parcialmente por segurança)
        if (response.has_token) {
          setConfig(prev => ({
            ...prev,
            system_token: '••••••••••' // Mascarar token por segurança
          }));
        }

        console.log(`✅ [South] Status verificado:`, response);
      }
    } catch (error: any) {
      console.error('❌ [South] Erro ao verificar status:', error);
      toast({
        title: "Erro",
        description: "Falha ao verificar status da integração",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (field: keyof SouthConfig, value: string) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const saveConfiguration = async () => {
    if (!config.system_token || !config.api_base_url) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha o token e a URL base da API",
        variant: "destructive"
      });
      return;
    }

    // Se o token está mascarado e não mudou, não enviar
    if (config.system_token === '••••••••••') {
      toast({
        title: "Token não alterado",
        description: "O token atual será mantido. Para alterar, digite um novo token.",
      });
      return;
    }

    try {
      setSaving(true);
      console.log(`💾 [South] Salvando configuração...`);

      const response = await api.post('/south/save-config', {
        client_id: client.id,
        system_token: config.system_token,
        api_base_url: config.api_base_url
      });

      if (response.success) {
        toast({
          title: "Configuração salva!",
          description: "Integração South configurada com sucesso",
        });
        
        // Atualizar status
        await checkIntegrationStatus();
        
        console.log(`✅ [South] Configuração salva com sucesso`);
      } else {
        throw new Error(response.error || 'Falha ao salvar configuração');
      }
    } catch (error: any) {
      console.error('❌ [South] Erro ao salvar:', error);
      toast({
        title: "Erro ao salvar",
        description: error.response?.data?.error || error.message || "Erro interno do servidor",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const clearConfiguration = async () => {
    // Confirmação dupla para evitar exclusões acidentais
    const confirmFirst = await confirm({
      title: '⚠️ Limpar Configuração South',
      description: 'Tem certeza que deseja limpar a configuração South?\n\nIsso irá:\n• Remover o token de autorização\n• Remover a URL da API\n• Desativar a integração South\n\nEsta ação não pode ser desfeita.',
      confirmText: 'Continuar',
      cancelText: 'Cancelar',
      variant: 'destructive'
    });

    if (!confirmFirst) return;

    const confirmSecond = await confirm({
      title: '🚨 ATENÇÃO: Confirmação Final',
      description: 'Esta é uma ação irreversível!\n\nDigite "CONFIRMAR" na próxima tela para proceder.',
      confirmText: 'CONFIRMAR',
      cancelText: 'Cancelar',
      variant: 'destructive'
    });

    if (!confirmSecond) return;

    const finalConfirm = await confirm({
      title: '🔐 Confirmação Final',
      description: 'Para confirmar a remoção da configuração South, digite "CONFIRMAR" (em maiúsculas):',
      confirmText: 'Confirmar Remoção',
      cancelText: 'Cancelar',
      variant: 'destructive',
      requireInput: true,
      inputLabel: 'Digite "CONFIRMAR" para prosseguir:',
      expectedInput: 'CONFIRMAR'
    });

    if (!finalConfirm) {
      toast({
        title: "Operação cancelada",
        description: "A configuração não foi removida.",
      });
      return;
    }

    try {
      setClearing(true);
      console.log(`🗑️ [South] Limpando configuração...`);

      const response = await api.delete(`/south/clear-config?client_id=${client.id}`, {
        data: { client_id: client.id }
      });

      if (response.success) {
        toast({
          title: "Configuração removida!",
          description: "A integração South foi limpa com sucesso. Você pode configurar uma nova integração.",
        });
        
        // Resetar estados
        setIntegrationStatus({
          active: false,
          has_token: false,
          has_api_url: false,
          system_management: ''
        });

        setConfig({
          system_token: '',
          api_base_url: ''
        });

        setTestResult(null);
        
        console.log(`✅ [South] Configuração limpa com sucesso`);
      } else {
        throw new Error(response.error || 'Falha ao limpar configuração');
      }
    } catch (error: any) {
      console.error('❌ [South] Erro ao limpar:', error);
      toast({
        title: "Erro ao limpar configuração",
        description: error.response?.data?.error || error.message || "Erro interno do servidor",
        variant: "destructive"
      });
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>Carregando status da integração...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status da Integração */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {integrationStatus.active ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600" />
                Integração South Ativa
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                Integração South Pendente
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {integrationStatus.active ? (
              <p className="text-green-600 font-medium">✅ Sistema South conectado e funcionando</p>
            ) : (
              <p className="text-gray-600">⚠️ Configure a integração South para começar</p>
            )}
            
            <div className="flex gap-2 mt-3">
              <Badge variant={integrationStatus.has_token ? "default" : "secondary"}>
                {integrationStatus.has_token ? "✅ Token" : "❌ Token"}
              </Badge>
              <Badge variant={integrationStatus.has_api_url ? "default" : "secondary"}>
                {integrationStatus.has_api_url ? "✅ URL API" : "❌ URL API"}
              </Badge>
              <Badge variant={integrationStatus.system_management === 'south' ? "default" : "secondary"}>
                {integrationStatus.system_management === 'south' ? "✅ South" : "❌ South"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuração da Integração */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configuração da Integração South
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            {/* Token de Autorização */}
            <div className="space-y-2">
              <Label htmlFor="system_token">Token de Autorização</Label>
              <Input
                id="system_token"
                type="password"
                placeholder="Digite o token de autorização da API South"
                value={config.system_token}
                onChange={(e) => handleConfigChange('system_token', e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-gray-500">
                Token fornecido pela South para autenticação na API
              </p>
            </div>

            {/* URL Base da API */}
            <div className="space-y-2">
              <Label htmlFor="api_base_url">URL Base da API</Label>
              <Input
                id="api_base_url"
                type="url"
                placeholder="https://api.autovaleprevencoes.org"
                value={config.api_base_url}
                onChange={(e) => handleConfigChange('api_base_url', e.target.value)}
              />
              <p className="text-xs text-gray-500">
                URL base do sistema South (ex: api.autovaleprevencoes.org)
              </p>
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={saveConfiguration}
              disabled={saving || !config.system_token || !config.api_base_url}
              className="flex items-center gap-2"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Database className="w-4 h-4" />
              )}
              Salvar Configuração
            </Button>

            {/* Botão Limpar Configuração - só aparece se houver configuração */}
            {integrationStatus.active && (
              <Button
                onClick={clearConfiguration}
                disabled={clearing}
                variant="destructive"
                className="flex items-center gap-2"
              >
                {clearing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Limpar Configuração
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialog de Confirmação */}
      <ConfirmationDialog />

    </div>
  );
};

export default SouthIntegration;
